import {FC, Fragment, useLayoutEffect, useRef, useState} from "react";
import {useWatch} from "react-hook-form";
import {NotificationConfig, useETANotificationContext} from "../ETANotificationConfigsProvider";
import {buildNotificationTimeline, MIN_GAP, MIN_LABEL_GAP_PX} from "../common/ETANotificationConfigs.utils";
import {COLORS} from "../../../../../constants/constants";
import {
  TimelineBand,
  TimelineCaption,
  TimelineContainer,
  TimelineDot,
  TimelineRail,
  TimelineRoleLabel,
  TimelineTrack,
  TimelineValueLabel
} from "../common/ETANotificationConfigs.style";

const FILL_COLOR: Record<"sent" | "window" | "eta", string> = {
  sent: COLORS.GRAY_800,
  window: COLORS.BLUE_500,
  eta: COLORS.TEXT_RED
};

export interface ETANotificationTimelineProps {
  /** Form field group to read (e.g. "firstNotification" / "secondNotification"). */
  fieldSuffix: string;
  /** Render grayed + non-interactive (mirrors the disabled state of the inputs above it). */
  disabled?: boolean;
}

/**
 * DP-3098 — feedback timeline shown under a notification's Trigger / Time-Window inputs. It reads the
 * block's live config and illustrates, against a fixed example ETA (1:00 PM), when the notification is
 * sent and the delivery window. Coincident markers merge into one dot with stacked labels. Hidden
 * until the config's numbers are valid.
 */
export const ETANotificationTimeline: FC<ETANotificationTimelineProps> = ({fieldSuffix, disabled}) => {
  const {
    etaFormState: {control}
  } = useETANotificationContext();
  const config = useWatch({control, name: fieldSuffix as any}) as NotificationConfig | undefined;

  // The declutter min-gap is a % of the rail, but labels are fixed-pixel — on narrow rails the
  // PO-chosen MIN_GAP buys fewer pixels than a label needs. Measure the rail and widen the gap so
  // adjacent labels always get MIN_LABEL_GAP_PX, keeping MIN_GAP as the wide-screen floor.
  const trackRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState(0);
  const minGap = trackWidth ? Math.max(MIN_GAP, (MIN_LABEL_GAP_PX / trackWidth) * 100) : MIN_GAP;

  const model = buildNotificationTimeline(config, minGap);

  // The timeline usually mounts hidden (empty config → no track), so re-measure when it appears.
  const isVisible = model !== null;
  useLayoutEffect(() => {
    if (!isVisible) return;
    const measure = () => setTrackWidth(trackRef.current?.offsetWidth ?? 0);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isVisible]);

  if (!model) return null;

  // Track must fit the tallest stacked-role marker: role labels start at 36px (rail 24 + 12 gap),
  // each line is 16px, plus a little bottom padding — so coincident 4-role dots don't overflow.
  const maxRoleLines = Math.max(...model.markers.map((marker) => marker.roleLabels.length));
  const trackHeight = 36 + maxRoleLines * 16 + 6;

  return (
    <TimelineContainer data-testid={`${fieldSuffix}-feedback-timeline`} isDisabled={disabled} aria-disabled={disabled}>
      <TimelineCaption>{model.caption}</TimelineCaption>
      <TimelineTrack ref={trackRef} trackHeight={trackHeight}>
        <TimelineRail>
          <TimelineBand
            from={model.notificationSentPosition}
            to={model.windowStartPosition}
            color={COLORS.BLUE_LIGHT}
          />
          <TimelineBand from={model.windowStartPosition} to={model.windowEndPosition} color={COLORS.BLUE_500} />
        </TimelineRail>

        {model.markers.map((marker) => (
          <Fragment key={marker.roleLabels.join("|")}>
            <TimelineDot
              left={marker.position}
              color={FILL_COLOR[marker.fill]}
              outlined={marker.outlined}
              data-testid={marker.outlined ? `${fieldSuffix}-sent-merged-dot` : undefined}
            />
            <TimelineValueLabel left={marker.position}>{marker.valueLabel}</TimelineValueLabel>
            <TimelineRoleLabel left={marker.position}>
              {marker.roleLabels.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </TimelineRoleLabel>
          </Fragment>
        ))}
      </TimelineTrack>
    </TimelineContainer>
  );
};

export default ETANotificationTimeline;
